import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { getAuthStatus, login, setupFirstAdmin, getErrorMessage } from '../api/auth';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, startSession } = useAuth();

  const [checkingStatus, setCheckingStatus] = useState(true);
  const [adminExists, setAdminExists] = useState(true); // true = mode connexion par défaut

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getAuthStatus()
      .then((res) => setAdminExists(res.admin_exists))
      .catch(() => setAdminExists(true)) // par sécurité, on suppose qu'un admin existe
      .finally(() => setCheckingStatus(false));
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Veuillez renseigner votre identifiant (nom d\'utilisateur ou email) et le mot de passe.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await login(username.trim(), password);
      startSession(res.access_token, { username: res.username, is_admin: res.is_admin });
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err, "Identifiant ou mot de passe incorrect."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Veuillez renseigner le nom d\'utilisateur et le mot de passe.');
      return;
    }
    if (username.trim().length < 3) {
      setError("Le nom d'utilisateur doit contenir au moins 3 caractères.");
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await setupFirstAdmin(username.trim(), password, email.trim());
      startSession(res.access_token, { username: res.username, is_admin: res.is_admin });
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err, "Impossible de créer le compte administrateur."));
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingStatus) {
    return <div className="auth-loading">Chargement…</div>;
  }

  const isSetupMode = !adminExists;

  return (
    <div className="auth-page">
      <form
        className="auth-card"
        onSubmit={isSetupMode ? handleSetup : handleLogin}
      >
        <div className="auth-header">
          <img src="/meapulse.png" alt="MEA PULSE" className="auth-logo" />
          <span className="scada-badge">S C A D A</span>
          <h1>{isSetupMode ? 'Configuration initiale' : 'Connexion'}</h1>
          <p>
            {isSetupMode
              ? "Aucun compte administrateur n'existe encore. Créez le premier compte pour accéder au système."
              : 'Connectez-vous pour accéder à la supervision MEA PULSE.'}
          </p>
        </div>

        {error && <div className="auth-error" role="alert" aria-live="polite">{error}</div>}

        <div className="auth-field">
          <label htmlFor="username">
            {isSetupMode ? "Nom d'utilisateur" : "Nom d'utilisateur ou email"}
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={isSetupMode ? 'ex : admin' : 'ex : admin ou admin@meapulse.ma'}
            aria-invalid={Boolean(error && !username.trim())}
          />
        </div>

        {isSetupMode && (
          <div className="auth-field">
            <label htmlFor="email">Email (optionnel)</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ex : admin@meapulse.ma"
            />
          </div>
        )}

        <div className="auth-field">
          <label htmlFor="password">Mot de passe</label>
          <input
            id="password"
            type="password"
            autoComplete={isSetupMode ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            aria-invalid={Boolean(error && !password)}
          />
        </div>

        {isSetupMode && (
          <div className="auth-field">
            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting
            ? 'Veuillez patienter…'
            : isSetupMode
            ? 'Créer le compte administrateur'
            : 'Se connecter'}
        </button>
      </form>
    </div>
  );
}
